import React from "react";
import QrReader from "react-qr-reader";
import { autoinject } from "aurelia-dependency-injection";
import { SettingsService } from "../_Services/SettingsService";
import { AppBar, Backdrop, CircularProgress, IconButton, LinearProgress, Toolbar, Typography } from "@material-ui/core";
import { observer } from "mobx-react";
import { SideMenu, SideMenuModel } from "../_Components/SideMenu";
import { action, observable, computed } from "mobx";
import { ICheckResponse, GateMode, ErrorState } from "../_Services/DataModel";
import { NativeAppService } from "../_Services/NativeAppService";
import { ScanCard } from "../_Components/ScanCard";
import { Spring } from "react-spring/renderprops.cjs";
import { GateService } from "../_Services/GateService";
import { AppDb } from "../_Services/AppDb";
import { ApiService } from "../_Services/ApiService";
import { AppModel } from "../AppModel";
import { LanguageSelector } from "../_Components/LanguageSelector";
import { ActionSwitcher } from "../_Components/ActionSwitcher";
import { uuid } from "@st/globster-data";

import ReadyToScanIcon from "@material-ui/icons/AspectRatio";
import IconMenu from "@material-ui/icons/Menu";
import OfflineIcon from "@material-ui/icons/WifiOff";
import OfflineGateIcon from "@material-ui/icons/CloudDownload";
import WifiIcon from "@material-ui/icons/Wifi";
import StorageIcon from "@material-ui/icons/Storage";
import AutoModeIcon from '@material-ui/icons/BrightnessAuto';
import ClockIcon from "@material-ui/icons/AccessAlarm";
import OfflineModeIcon from "@material-ui/icons/Cloud";

// only the newest scans stay on screen, older ones are dropped
const maxVisibleScanCards = 3;

export interface ScanCardInfo {
    id: string;
    checkInfo: ICheckResponse;
    code: string;
    isPreviewOpen: boolean;
    previewTimeout: number | null;
}

@autoinject()
export class ScanPageModel {
    constructor(
        public settingsService: SettingsService,
        private gateService: GateService,
        nativeAppService: NativeAppService,
        private appDb: AppDb,
        private apiService: ApiService) {

        nativeAppService.nfcScanned.subscribe(async (code) => {
            await this.scan(code);
        });

        appDb.getOrderedChecksDesc().then(history => {
            const lastCheckData = history[0];

            if (lastCheckData) {
                const lastScanCardInfo: ScanCardInfo = {
                    id: uuid(),
                    checkInfo: lastCheckData,
                    code: lastCheckData.BadgeId,
                    isPreviewOpen: false,
                    previewTimeout: null,
                };

                this.scanCardsInfo.push(lastScanCardInfo);
            }
        });
    }

    @observable scanCardsInfo: ScanCardInfo[] = [];
    @observable isScanAvailable: boolean = true;
    @observable isLoading: boolean = false;
    @observable isError: boolean = false;

    @action.bound changeDirection() {
        const directions = this.settingsService.getAllowedCheckTypes();
        const checkTypeId = this.settingsService.checkTypeId;
        let idx = 0;

        if (checkTypeId) {
            idx = (directions.findIndex(i => i.value === checkTypeId) + 1);
        }

        if (directions && idx >= directions.length) {
            idx = 0;
        }

        this.settingsService.checkTypeId = directions[idx].value;
    }

    @action async scan(code: string) {
        this.isLoading = true;

        try {
            const lastScanCardIndex = this.scanCardsInfo.length - 1;

            // allow same scan codes only if not locked or first scan
            if (code !== this.scanCardsInfo[lastScanCardIndex]?.code || this.isScanAvailable) {
                this.isScanAvailable = false;

                const response = await this.gateService.checkBadge(code);
                const validationTimeout = response.IsValid
                    ? this.settingsService.scanOnScreenTimeout
                    : this.settingsService.errorOnScreenTimeout;

                const currentScanCardInfo: ScanCardInfo = {
                    id: uuid(),
                    checkInfo: response,
                    code: code,
                    isPreviewOpen: false,
                    previewTimeout: null,
                };
                this.scanCardsInfo.push(currentScanCardInfo);
                this.trimScanCards();
                this.openPreview(currentScanCardInfo.id, validationTimeout);

                this.isError = false;
            }
            
            this.refreshLockTimeout();
        } catch (e) {
            this.isError = true;
            this.closeAllPreviews();

            setTimeout(() => {
                this.isError = false;
                this.isScanAvailable = true;
            }, this.settingsService.errorOnScreenTimeout);
        } finally {
            this.isLoading = false;
        }
    }

    // only one scan detail may be open at a time, so a fast scan closes the previous one
    @action openPreview(scanCardId: string, previewTimeout: number) {
        this.closeAllPreviews();

        const scanCardToOpen = this.scanCardsInfo.find(i => i.id === scanCardId);

        if (!scanCardToOpen) {
            return;
        }

        scanCardToOpen.isPreviewOpen = true;
        scanCardToOpen.previewTimeout = setTimeout(() => this.closePreview(scanCardId), previewTimeout);
    }

    @action.bound closePreview(scanCardId: string) {
        const scanCardToClose = this.scanCardsInfo.find(i => i.id === scanCardId);

        if (scanCardToClose) {
            this.clearPreviewTimeout(scanCardToClose);
            scanCardToClose.isPreviewOpen = false;
        }
    }

    @action.bound togglePreview(scanCardId: string) {
        const scanCardToToggle = this.scanCardsInfo.find(i => i.id === scanCardId);

        if (!scanCardToToggle) {
            return;
        }

        if (scanCardToToggle.isPreviewOpen) {
            this.closePreview(scanCardId);
        } else {
            this.openPreview(scanCardId, this.settingsService.scanOnScreenTimeout);
        }
    }

    @action private closeAllPreviews() {
        this.scanCardsInfo.forEach(scanCardInfo => {
            this.clearPreviewTimeout(scanCardInfo);
            scanCardInfo.isPreviewOpen = false;
        });
    }

    private clearPreviewTimeout(scanCardInfo: ScanCardInfo) {
        if (scanCardInfo.previewTimeout) {
            clearTimeout(scanCardInfo.previewTimeout);
            scanCardInfo.previewTimeout = null;
        }
    }

    private trimScanCards() {
        while (this.scanCardsInfo.length > maxVisibleScanCards) {
            const droppedScanCard = this.scanCardsInfo.shift();

            if (droppedScanCard) {
                this.clearPreviewTimeout(droppedScanCard);
            }
        }
    }

    private cancelTimeout: () => void = () => { };

    private refreshLockTimeout() {
        this.cancelTimeout();
        const timer = setTimeout(() => {
            this.isScanAvailable = true;
        }, this.settingsService.scanningLockTimeout);
        this.cancelTimeout = () => clearTimeout(timer);
    }

    @computed get isNoConnection(): boolean {
        return this.settingsService.gateMode === GateMode.Online && !this.apiService.canSend;
    }

    @computed get isDbWarning(): boolean {
        return this.appDb.dbQuotaDisplayWarning;
    }

    @computed get isOfflineSupported(): boolean {
        return this.settingsService.gate && this.settingsService.gate.IsOfflineModeSupported;
    }
}

export interface ScanPageProps {
    model: ScanPageModel;
    menuModel: SideMenuModel;
    appModel: AppModel;
}

@observer
export class ScanPage extends React.Component<ScanPageProps> {
    constructor(props: ScanPageProps, context: any) {
        super(props, context);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.requestWakeLock = this.requestWakeLock.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    componentDidMount() {
        document.addEventListener("keydown", this.handleKeyDown);

        if ("wakeLock" in navigator && !this.props.model.settingsService.isCameraScanningEnabled) {
            this.requestWakeLock();
            document.addEventListener("visibilitychange", this.handleVisibilityChange);
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);

        if (this.wakeLock) {
            document.removeEventListener("visibilitychange", this.handleVisibilityChange);
            this.wakeLock.release();
            this.wakeLock = null;
        }
    }

    private buffer: string = "";
    private wakeLock: any = null;

    @action.bound private handleCameraScan = async (data) => {
        if (!this.props.model.isLoading && data) {
            await this.props.model.scan(data);
        }
    }

    @action.bound private handleCameraError = err => {
        console.error(err);
    }

    async handleKeyDown(e: KeyboardEvent) {
        if (this.props.appModel.appState.globalError.value) {
            this.buffer = "";
            return;
        }

        if (e.keyCode === 27) {
            this.buffer = "";
        }

        if (e.keyCode === 13 && this.buffer) {
            if (!this.props.model.isLoading) {
                await this.props.model.scan(this.buffer);
            }
            this.buffer = "";
        }

        if (e.key && e.key.length === 1 && /[a-z,A-Z,0-9]/.test(e.key)) {
            this.buffer = this.buffer + e.key;
        }
    }

    getCardAnimationMaxHeight(isPreviewOpen: boolean) {
        if (isPreviewOpen) {
            return { maxHeight: window.innerHeight };
        }
        return { maxHeight: 200 };
    }

    // index 0 is the newest scan; the depth class lets css drop cards that no longer fit
    getCardCssClasses(cardInfo: ScanCardInfo, index: number, isAnimationEnabled: boolean): string {
        const cssClasses = ["card-preview", `depth-${index}`];

        if (index === 0) {
            cssClasses.push("latest");

            if (isAnimationEnabled) {
                cssClasses.push("pop-anim");
            }
        }

        if (cardInfo.isPreviewOpen) {
            cssClasses.push("open");
        }

        return cssClasses.join(" ");
    }

    getCardAnimationConfiguration(isAnimationEnabled: boolean) {
        if (isAnimationEnabled) {
            return { tension: 1200, friction: 80, precision: 0.1 };
        }
        return { tension: 0, friction: 10, precision: 0.1 };
    }

    async requestWakeLock() {
        try {
            this.wakeLock = await (window.navigator as any).wakeLock.request("screen");
        } catch (err) {
            console.error(`${err.name}, ${err.message}`);
        }
    }

    async handleVisibilityChange() {
        if (this.wakeLock !== null && document.visibilityState === "visible") {
            await this.requestWakeLock();
        }
    }

    render() {
        const { model, menuModel, appModel } = this.props;

        return <div className="mdl-layout mdl-js-layout mdl-layout--fixed-header mdl-layout--no-drawer-button full-height">
            <SideMenu model={menuModel} />
            <AppBar position="static">
                <Toolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <IconButton color="inherit" aria-label="Menu" onClick={_ => menuModel.isMenuOpen = true}>
                        <IconMenu />
                    </IconButton>
                    <div className="toolbar-statuses">
                        {
                            model.settingsService.showGateType &&
                            <>
                                {
                                    model.isOfflineSupported
                                        ? <OfflineGateIcon />
                                        : <WifiIcon />
                                }
                            </>
                        }
                        {
                            model.isOfflineSupported &&
                            model.settingsService.isAutomativeMode &&
                            <AutoModeIcon className="blink-anim" />
                        }
                        {
                            model.isDbWarning && <StorageIcon className="blink-anim" />
                        }
                    </div>
                    {
                        model.settingsService.gate &&
                        <Typography variant="h6" color="inherit">
                            {
                                model.settingsService.gate.Name
                            }
                        </Typography>
                    }
                    <div className={"toolbar-switchers"}>
                        {
                            model.settingsService.showLanguageSelector && <LanguageSelector />
                        }
                        {
                            model.settingsService.showScanningMode &&
                            <ActionSwitcher settingsService={model.settingsService} onClick={() => model.changeDirection()} />
                        }
                    </div>
                </Toolbar>
            </AppBar>
            {
                model.isLoading
                    ? <LinearProgress />
                    : <div className="progress-space" />
            }
            <div className={`status-icons-wrapper ${model.settingsService.isCameraScanningEnabled && "small"}`}>
                {
                    appModel.appState.globalError.value !== ErrorState.None
                        ? <div className="status-icons error" >
                            {
                                appModel.appState.globalError.hasFlag(ErrorState.TimeNotSynced) &&
                                <ClockIcon className="container-big blink-anim" />
                            }
                            {
                                appModel.appState.globalError.hasFlag(ErrorState.DbQuotaReached) &&
                                <StorageIcon className="container-big blink-anim" />
                            }
                        </div>
                        : <div className="status-icons" >
                            {
                                model.isLoading && <CircularProgress className="container-big" />
                            }
                            {
                                !model.isLoading && !model.isError && !model.isNoConnection &&
                                <ReadyToScanIcon className="container-big puls-anim" />
                            }
                            {
                                (model.isError || model.isNoConnection) &&
                                <OfflineIcon className="container-big blink-anim" />
                            }
                            {
                                model.settingsService.gateMode === GateMode.Offline &&
                                <OfflineModeIcon className="container-big puls-anim" />
                            }
                        </div>
                }
            </div>
            {
                model.settingsService.showEventName &&
                <div className="mdl-layout__content">
                    <div className="layout-form">
                        {
                            model.settingsService.homeImages.map((i, idx) => <img key={idx} src={i} />)
                        }
                        {
                            model.settingsService.homeTitles.map((i, idx) => <div className="title" key={idx}>{i}</div>)
                        }
                    </div>
                </div>
            }
            {
                model.settingsService.isCameraScanningEnabled &&
                <div className="camera-wrapper">
                    <QrReader
                        delay={300}
                        onError={this.handleCameraError}
                        onScan={this.handleCameraScan}
                        style={{ width: "100%" }}
                    />
                </div>
            }
            {/* newest scan first, so it keeps its place when the row gets too wide */}
            <div className="cards-container">
                {
                    model.scanCardsInfo.slice().reverse().map((cardInfo, index) =>
                        <Spring
                            key={`preview-${cardInfo.id}`}
                            to={this.getCardAnimationMaxHeight(cardInfo.isPreviewOpen)}
                            config={this.getCardAnimationConfiguration(model.settingsService.isAnimationEnabled)}
                        >
                            {
                                props => <>
                                    {
                                        cardInfo.isPreviewOpen &&
                                        <Backdrop
                                            open={cardInfo.isPreviewOpen}
                                            onClick={_ => model.closePreview(cardInfo.id)}
                                            style={{ zIndex: 1200, transition: "0" }}
                                        />
                                    }
                                    <ScanCard
                                        getResponse={() => cardInfo.checkInfo}
                                        className={this.getCardCssClasses(cardInfo, index, model.settingsService.isAnimationEnabled)}
                                        settingsService={model.settingsService}
                                        headerClick={_ => model.togglePreview(cardInfo.id)}
                                        close={() => model.closePreview(cardInfo.id)}
                                        style={{ maxHeight: props.maxHeight }}
                                    />
                                </>
                            }
                        </Spring>
                    )
                }
            </div>
        </div>;
    }
}